/**
 * fracasEngine.js — FRACAS (Failure Reporting, Analysis & Corrective Action System)
 *
 * OAV-053 (final): Closes the Isograph AWB 4.0 FRACAS capability gap. Provides the
 * canonical FRACAS workflow per MIL-HDBK-2155 and IEC 62740 §B for failure-reporting
 * lifecycle, root-cause classification, corrective-action tracking and Pareto
 * trending.
 *
 * Workflow stages:
 *   1. Report      — failure observed, captured against an equipment + failure mode
 *   2. Analyse     — RCA assignment (5-Why / Ishikawa / FTA pointer)
 *   3. Correct     — interim and permanent corrective actions with owners
 *   4. Verify      — effectiveness review against agreed close-out criteria
 *   5. Close       — record sealed; feeds the recurrence trend
 *
 * No dependencies. Pure ES module. Persistence is the caller's responsibility —
 * reports are returned as plain JSON suitable for projectData.fracasReports.
 */

export const FRACAS_STAGES = ['Report', 'Analyse', 'Correct', 'Verify', 'Close'];

export const RCA_METHODS = ['5-Why', 'Ishikawa', 'FaultTree', 'BowTie', 'BarrierAnalysis'];

export const CORRECTIVE_ACTION_TYPES = [
  'design-change', 'procedure-change', 'training', 'spare-strategy',
  'maintenance-task', 'supplier-change', 'no-action-justified'
];

/**
 * FRACAS report record schema.
 * Returned by createReport(); consumers spread into projectData.fracasReports.
 */
export function createReport({ equipmentId, failureModeId, observedAt, reporter,
  description, severity = 5, occurrence = 3, detection = 5,
  immediateAction = '', evidenceLinks = [] } = {}) {
  if (!equipmentId) throw new Error('FRACAS createReport: equipmentId is required');
  if (!description || description.trim().length < 5)
    throw new Error('FRACAS createReport: description must be at least 5 characters');

  const id = `FRACAS-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  return {
    id,
    equipmentId,
    failureModeId: failureModeId || null,
    observedAt: observedAt || new Date().toISOString(),
    reporter: reporter || '',
    description: description.trim(),
    // Reuse FMECA S/O/D so the report can be ranked alongside criticality.
    severity, occurrence, detection,
    rpn: severity * occurrence * detection,
    immediateAction,
    evidenceLinks,
    stage: 'Report',
    rca: null,                    // populated in Analyse stage
    correctiveActions: [],        // populated in Correct stage
    verification: null,           // populated in Verify stage
    closedAt: null,
    auditTrail: [{ stage: 'Report', at: new Date().toISOString(), by: reporter || '' }],
  };
}

/**
 * Move report into Analyse — record root-cause method + findings.
 */
export function attachRCA(report, { method, findings, faultTreeRef = null, by = '' }) {
  if (!report) throw new Error('FRACAS attachRCA: report missing');
  if (!RCA_METHODS.includes(method))
    throw new Error(`FRACAS attachRCA: unsupported method "${method}". Use one of ${RCA_METHODS.join(', ')}`);
  if (!findings || findings.trim().length < 10)
    throw new Error('FRACAS attachRCA: findings must be at least 10 characters');

  return {
    ...report,
    stage: 'Analyse',
    rca: { method, findings: findings.trim(), faultTreeRef, by, at: new Date().toISOString() },
    auditTrail: [...report.auditTrail, { stage: 'Analyse', at: new Date().toISOString(), by }],
  };
}

/**
 * Add a corrective action; first call moves report to Correct stage.
 */
export function addCorrectiveAction(report, { type, description, owner, dueDate, by = '' }) {
  if (!CORRECTIVE_ACTION_TYPES.includes(type))
    throw new Error(`FRACAS addCorrectiveAction: unsupported type "${type}"`);
  if (!description || !owner || !dueDate)
    throw new Error('FRACAS addCorrectiveAction: description, owner, dueDate are required');

  const ca = {
    id: `CA-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    type, description: description.trim(), owner, dueDate,
    status: 'open', completedAt: null, completedBy: null,
  };
  const stage = report.stage === 'Analyse' ? 'Correct' : report.stage;
  return {
    ...report,
    stage,
    correctiveActions: [...report.correctiveActions, ca],
    auditTrail: stage !== report.stage
      ? [...report.auditTrail, { stage, at: new Date().toISOString(), by }]
      : report.auditTrail,
  };
}

/**
 * Mark a corrective action complete.
 */
export function completeCorrectiveAction(report, caId, { by = '' } = {}) {
  return {
    ...report,
    correctiveActions: report.correctiveActions.map(ca =>
      ca.id === caId
        ? { ...ca, status: 'complete', completedAt: new Date().toISOString(), completedBy: by }
        : ca),
  };
}

/**
 * Record effectiveness verification — moves report to Verify stage.
 */
export function recordVerification(report, { effective, evidence, observationWindowDays = 30, by = '' }) {
  if (typeof effective !== 'boolean')
    throw new Error('FRACAS recordVerification: effective must be boolean');
  return {
    ...report,
    stage: 'Verify',
    verification: { effective, evidence: evidence || '', observationWindowDays, by, at: new Date().toISOString() },
    auditTrail: [...report.auditTrail, { stage: 'Verify', at: new Date().toISOString(), by }],
  };
}

/**
 * Close report — only allowed when verification.effective === true.
 */
export function closeReport(report, { by = '' } = {}) {
  if (!report.verification || report.verification.effective !== true)
    throw new Error('FRACAS closeReport: cannot close until verification.effective === true');
  const closedAt = new Date().toISOString();
  return {
    ...report,
    stage: 'Close',
    closedAt,
    auditTrail: [...report.auditTrail, { stage: 'Close', at: closedAt, by }],
  };
}

/**
 * Pareto trending: counts failures per equipmentId, sorts descending.
 * Returns [{equipmentId, count, cumulativeShare}], cumulativeShare in [0,1].
 */
export function paretoByEquipment(reports = []) {
  const counts = new Map();
  for (const r of reports) counts.set(r.equipmentId, (counts.get(r.equipmentId) || 0) + 1);
  const total = [...counts.values()].reduce((a, b) => a + b, 0) || 1;
  const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]);
  let running = 0;
  return sorted.map(([equipmentId, count]) => {
    running += count;
    return { equipmentId, count, cumulativeShare: running / total };
  });
}

/**
 * Recurrence detection — same equipment + same failure mode within `windowDays`
 * after a previous Close. A non-empty result is a signal that the corrective
 * action did not stick.
 */
export function findRecurrences(reports = [], windowDays = 180) {
  const closedByKey = new Map();   // equipmentId|failureModeId -> [closedAtMs...]
  const recurrences = [];
  const sorted = [...reports].sort((a, b) => new Date(a.observedAt) - new Date(b.observedAt));
  const windowMs = windowDays * 24 * 60 * 60 * 1000;
  for (const r of sorted) {
    const key = `${r.equipmentId}|${r.failureModeId || ''}`;
    const closures = closedByKey.get(key) || [];
    const observedMs = new Date(r.observedAt).getTime();
    for (const cl of closures) {
      if (observedMs > cl && observedMs - cl <= windowMs) {
        recurrences.push({ reportId: r.id, equipmentId: r.equipmentId,
          failureModeId: r.failureModeId, closedAt: new Date(cl).toISOString(),
          recurredAt: r.observedAt, daysAfterClose: (observedMs - cl) / (24 * 60 * 60 * 1000) });
      }
    }
    if (r.stage === 'Close' && r.closedAt) {
      closures.push(new Date(r.closedAt).getTime());
      closedByKey.set(key, closures);
    }
  }
  return recurrences;
}

/**
 * Validate a report against MIL-HDBK-2155 minimum data set.
 * Returns {valid, missing: [field, ...]}.
 */
export function validateReport(report) {
  const required = ['equipmentId', 'description', 'observedAt', 'severity', 'occurrence', 'detection'];
  const missing = required.filter(k => !report[k] && report[k] !== 0);
  return { valid: missing.length === 0, missing };
}

export default {
  FRACAS_STAGES,
  RCA_METHODS,
  CORRECTIVE_ACTION_TYPES,
  createReport,
  attachRCA,
  addCorrectiveAction,
  completeCorrectiveAction,
  recordVerification,
  closeReport,
  paretoByEquipment,
  findRecurrences,
  validateReport,
};
